import chroma from 'chroma-js';

export function useTableConditionalFormatter() {
  const COMMON_OPERATORS = [
    { value: 'empty', label: 'empty' },
    { value: 'not_empty', label: 'not empty' },
    { value: 'equal', label: 'equal to', notation: '=' },
    { value: 'not_equal', label: 'not equal to', notation: '!=' },
  ];

  const NUMERIC_OPERATORS = [
    { value: 'less_than', label: 'less than', notation: '<' },
    { value: 'greater_than', label: 'greater than', notation: '>' },
    { value: 'less_than_or_equal', label: 'less than or equal to', notation: '<=' },
    { value: 'greater_than_or_equal', label: 'greater than or equal to', notation: '>=' },
  ];

  const STRING_OPERATORS = [
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'ends_with', label: 'ends with' },
  ];

  // merge all operators
  const ALL_OPERATORS = [
    ...COMMON_OPERATORS,
    ...NUMERIC_OPERATORS,
    ...STRING_OPERATORS,
  ];

  const OPERATOR_LABEL_MAP = Object.fromEntries(
    ALL_OPERATORS.map(op => [op.value, op]),
  );

  function getOperators(field) {
    if (['integer', 'numeric', 'float'].includes(field.field_type)) {
      return [...COMMON_OPERATORS, ...NUMERIC_OPERATORS];
    }
    return [...COMMON_OPERATORS, ...STRING_OPERATORS];
  }

  function parseCellValue(cell_value, cell_type) {
    if (['integer', 'numeric', 'float'].includes(cell_type) && cell_value?.includes(','))
      return cell_value.replace(/,/g, '');
    return cell_value;
  }

  function checkRuleMatch(rule, row_data, column_data, cell_value, table_columns_map) {
    if (rule.apply_to === 'entire_row') {
      return checkRowMatch(rule, row_data, table_columns_map);
    }
    else {
      if (rule.field !== column_data.field)
        return false;
      const column = table_columns_map[column_data.field];
      const cell_type = column?.type || column?.field_type;
      const parsed_cell_value = parseCellValue(cell_value, cell_type);
      return checkCellMatch(rule, parsed_cell_value);
    }
  }

  function checkRowMatch(rule, row_data, table_columns_map) {
    const cell_type = table_columns_map[rule.field]?.type;
    const cell_value = parseCellValue(row_data[rule.field], cell_type);

    return checkCellMatch(rule, cell_value);
  }
  function checkCellMatch(rule, cell_value) {
    if (rule.operator === 'empty') {
      return [null, undefined, '', 'null'].includes(cell_value);
    }
    else if (rule.operator === 'not_empty') {
      return ![null, undefined, '', 'null'].includes(cell_value);
    }
    if (!cell_value)
      return false;
    if (rule.operator === 'equal') {
      return cell_value === rule.value;
    }
    else if (rule.operator === 'not_equal') {
      return cell_value !== rule.value;
    }
    // For numeric operators
    else if (['less_than', 'greater_than', 'less_than_or_equal', 'greater_than_or_equal'].includes(rule.operator)) {
      const left = Number(cell_value);
      const right = Number(rule.value);
      if (Number.isNaN(left) || Number.isNaN(right))
        return false;
      if (rule.operator === 'less_than') {
        return left < right;
      }
      else if (rule.operator === 'greater_than') {
        return left > right;
      }
      else if (rule.operator === 'less_than_or_equal') {
        return left <= right;
      }
      else if (rule.operator === 'greater_than_or_equal') {
        return left >= right;
      }
    }

    // For text operators
    else if (rule.operator === 'contains') {
      return String(cell_value).toLowerCase().includes(String(rule.value).toLowerCase());
    }
    else if (rule.operator === 'not_contains') {
      return !String(cell_value).toLowerCase().includes(String(rule.value).toLowerCase());
    }
    else if (rule.operator === 'starts_with') {
      return String(cell_value).toLowerCase().startsWith(String(rule.value).toLowerCase());
    }
    else if (rule.operator === 'ends_with') {
      return String(cell_value).toLowerCase().endsWith(String(rule.value).toLowerCase());
    }

    return false;
  }
  function getContrastTextColor(color) {
    try {
      const white_bias = 5;
      return (chroma.contrast(color, 'white') + white_bias) >= chroma.contrast(color, 'black') ? '#FFFFFF' : '#000000';
    }
    catch (error) {
      logger.log(`[DEBUG] bi-table-conditional-formatter.composable.js::140\n${error}`);
      return '#000000';
    }
  }
  function getLighterColor(color) {
    return chroma(color).luminance(
      Math.min(1, chroma(color).luminance() + 0.2),
    ).hex();
  }
  function getColorForValue(value, min, max, baseColor, steps) {
    const colors = chroma
      .scale(['#ffffff', baseColor])
      .mode('lch')
      .colors(steps);

    let index;

    if (max === min) {
      // All values are the same — return mid color or last
      index = Math.floor(steps / 2);
    }
    else {
      const ratio = (value - min) / (max - min);
      index = Math.round(ratio * (steps - 1));
    }

    const safeIndex = Math.max(0, Math.min(index, steps - 1));
    return colors[safeIndex];
  }
  return {
    OPERATOR_LABEL_MAP,
    getOperators,
    checkRuleMatch,
    getContrastTextColor,
    getLighterColor,
    getColorForValue,
  };
}
